/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { FilterProps, TableHeader } from 'ogc-processes-components';

import { DataTable, DataTableFilterMeta, DataTableFilterMetaData } from 'primereact/datatable';
import { Column, ColumnProps } from 'primereact/column';
import { usePagination } from '@/hooks/usePagination.tsx';

import { FilterMatchMode } from 'primereact/api';
import { classNames } from 'primereact/utils';

import { MonitorProcessData, MonitorProcessJobs } from '@/types/models.ts';
import { useTranslation } from 'react-i18next';
import { ProjectInfo } from './JobsMonitorTable/ProjectInfo';
import { ProcessInfo } from './JobsMonitorTable/ProcessInfo';
import { ProcessDescription } from './JobsMonitorTable/ProcessDescription';
import { JobsRunCounter } from './JobsMonitorTable/JobsRunCounter';
import { DateTime } from 'luxon';
import { DateRender } from './JobsMonitorTable/DateRender';
import { JobsInfo } from './JobsMonitorTable/JobsInfo';
import { ActionRender } from './JobsMonitorTable/ActionRender';

interface JobsMonitorTableProps extends FilterProps {
  monitorData: MonitorProcessJobs | undefined;
  queryParamIsLoaded: boolean;
  getMonitorData: (query?: {
    page?: number;
    limit?: number;
    runs?: number;
    querySearchText?: string;
  }) => Promise<MonitorProcessJobs>;
  loading: boolean;
  className?: string;
}

const defaultFilters = (filterProperties?: Record<string, unknown>): DataTableFilterMeta => {
  return {
    global: { value: filterProperties?.global ?? null, matchMode: FilterMatchMode.CONTAINS }
  };
};

export const JobsMonitorTable: React.FC<JobsMonitorTableProps> = ({ className, ...props }) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters());

  const [globalFilterValue, setGlobalFilterValue] = useState<string>('');

  const deferredGlobalFilter = useDeferredValue(globalFilterValue);

  const initFilters = (_defaultFilters?: Record<string, unknown>) => {
    const _filters = defaultFilters(_defaultFilters);
    setFilters(_filters);
    const global = _filters.global as DataTableFilterMetaData;
    setGlobalFilterValue(global.value);
  };

  useEffect(() => {
    if (!props.queryParamIsLoaded) {
      initFilters(props.defaultFilters);
    }
  }, [props.defaultFilters, props.queryParamIsLoaded]);

  const paginationInfo = useMemo(() => {
    if (props.monitorData) {
      return {
        currentPage: props.monitorData.pagination.page,
        rows: props.monitorData.pagination.limit,
        pages: props.monitorData.pagination['total-pages']
      };
    }
    return {
      currentPage: 1,
      rows: 5,
      pages: 1
    };
  }, [props.monitorData]);

  const { paginatorTemplate } = usePagination(paginationInfo, props.getMonitorData);

  const monitorDataColumns = useMemo(() => {
    return [
      {
        field: 'project',
        order: 0,
        header: 'Project',
        style: { width: '12%' },
        body: (data: MonitorProcessData) => {
          if (data.id.startsWith('dyna~')) {
            return 'Dyna process';
          }
          return <ProjectInfo monitorData={data} />;
        }
      },
      {
        field: 'process_id',
        order: 1,
        header: 'Process ID',
        style: { width: '10%' },
        body: (data: MonitorProcessData) => {
          return <ProcessInfo monitorData={data} />;
        }
      },
      {
        field: 'process_title',
        header: 'Process title',
        style: { width: '20%' },
        order: 2,
        body: (data: MonitorProcessData) => {
          return <ProcessDescription monitorData={data} />;
        }
      },
      {
        field: 'runs',
        header: 'Runs',
        align: 'center',
        order: 3,
        style: { width: '15%' },
        body: (data: MonitorProcessData) => {
          return <JobsRunCounter monitorData={data} />;
        }
      },
      {
        field: 'total',
        header: 'Total',
        align: 'center',
        order: 4,
        className: 'font-bold',
        style: { width: '5%' }
      },
      {
        field: 'last_finished',
        header: 'Last Finished',
        style: { width: '10%' },
        body: ({ runs }: MonitorProcessData) => {
          const last_runs = runs
            .filter((el) => !!el.finished)
            .toSorted((a, b) => DateTime.fromISO(b.finished!).diff(DateTime.fromISO(a.finished!)).seconds ?? 0);
          const last_run = last_runs[0];
          return <DateRender date={last_run?.finished} />;
        }
      },
      {
        field: 'last_runs',
        header: 'Last runs',
        body: (data: MonitorProcessData) => {
          return <JobsInfo monitorData={data} maxPerLine={20} size={15} spacing={5} />;
        }
      },
      {
        header: 'Actions',
        body: (data: MonitorProcessData) => {
          return <ActionRender monitorData={data} />;
        }
      }
    ] as (ColumnProps & { order: number })[];
  }, []);

  return (
    <div className="w-full">
      <DataTable
        className={classNames('text-sm', className)}
        loading={props.loading}
        value={props.monitorData?.processes ?? []}
        header={
          <TableHeader
            onRefresh={{
              command: () => {
                const query: Record<string, string> = {
                  page: paginationInfo?.currentPage.toString(),
                  limit: paginationInfo?.rows.toString()
                };
                if (globalFilterValue) {
                  query['querySearchText'] = globalFilterValue;
                }

                return props.getMonitorData(query);
              }
            }}
            onClearFilters={{
              severity: props.onClearFilters?.severity,
              command: () => {
                initFilters();
                props.onClearFilters?.command?.(defaultFilters());
              }
            }}
            filters={filters}
            setFilters={(filters) => {
              setFilters(filters);
              props.handleGlobalFilters?.(filters);
            }}
            globalFilterValue={deferredGlobalFilter}
            setGlobalFilterValue={setGlobalFilterValue}
          />
        }
        dataKey="link"
        stripedRows
        paginator
        rows={paginationInfo.rows}
        pageLinkSize={paginationInfo.pages}
        paginatorTemplate={paginatorTemplate}
      >
        {monitorDataColumns
          .toSorted((a, b) => (a.order ?? 20) - (b.order ?? 20))
          .map((column) => ({
            ...column,
            header: t([`jobs.properties.${column.field}`, `${column.header}`])
          }))
          .map((el, key) => (
            <Column key={key} {...el} />
          ))}
      </DataTable>
    </div>
  );
};
